import React, { useState } from 'react';
import { Table } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import PencilIcon from './icons/PencilIcon';
import TrashIcon from './icons/TrashIcon';
import UserData from '../types/IUserResponse';
import useDeleteUser from '../hooks/reactQuery/useDeleteUser';
import DeleteConfirmationModal from './modals/DeleteConfirmationModal';
import UserTableFilters from './UserTableFilters';
import ChangePasswordIcon from './icons/ChangePasswordIcon';
import FilterBar, { FilterChip } from './FilterBar';
import TablePagination, {
  PageSizeChoice,
  tableMinHeight,
} from './TablePagination';
import TableStateRow from './TableStateRow';
import getInitials from '../utils/getInitials';
import useUrlFilters from '../hooks/useUrlFilters';
import { track } from '../telemetry';

interface UserTableProps {
  data: UserData | undefined;
  itemsPerPage: number;
  handleModalOpen: (data: any) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  isFetching: boolean;
  firstName: string;
  lastName: string;
  setFirstName: (e: any) => void;
  setLastName: (e: any) => void;
  setEmail: (e: any) => void;
  email: string;
  setRoles: (e: any) => void;
  roles: string[];
  onPageSizeChange?: (size: PageSizeChoice) => void;
  isAutoPageSize?: boolean;
  fitPageSize?: number;
}

const COLUMNS = 4;

const UserTable: React.FC<UserTableProps> = ({
  data,
  handleModalOpen,
  itemsPerPage,
  currentPage,
  setCurrentPage,
  isFetching,
  roles,
  setRoles,
  email,
  setLastName,
  setEmail,
  setFirstName,
  firstName,
  lastName,
  onPageSizeChange,
  isAutoPageSize,
  fitPageSize,
}) => {
  const { user: currentUser } = useAuth0();
  const [clearSignal, setClearSignal] = useState(0);
  const [deleteId, setDeleteId] = useState('');
  const { mutate: deleteUser, isLoading: isDeleting } = useDeleteUser(deleteId);
  const [roleItems, setRoleItems] = useState<string[]>(roles ?? []);

  useUrlFilters({
    page: { kind: 'page', value: currentPage, set: setCurrentPage },
    firstName: { kind: 'text', value: firstName, set: setFirstName },
    lastName: { kind: 'text', value: lastName, set: setLastName },
    email: { kind: 'text', value: email, set: setEmail },
    role: {
      kind: 'list',
      value: roles,
      set: (next: string[]) => {
        setRoles(next);
        setRoleItems(next);
      },
    },
  });

  const handleDelete = (userId: string) => {
    deleteUser(
      { userId },
      {
        onSuccess: () => {
          track('user_deleted', {});
          toast.success(`User Deleted`);
          setDeleteId('');
        },
        onError: () => {
          toast.error(`Failed to delete`);
        },
      },
    );
  };

  const chips: FilterChip[] = [];
  if (firstName) {
    chips.push({
      key: 'firstName',
      label: 'First name',
      value: firstName,
      onClear: () => setFirstName(''),
    });
  }
  if (lastName) {
    chips.push({
      key: 'lastName',
      label: 'Last name',
      value: lastName,
      onClear: () => setLastName(''),
    });
  }
  if (email) {
    chips.push({
      key: 'email',
      label: 'Email',
      value: email,
      onClear: () => setEmail(''),
    });
  }
  if (roles?.length) {
    chips.push({
      key: 'role',
      label: 'Role',
      value: roles.join(', '),
      onClear: () => {
        setRoles([]);
        setRoleItems([]);
      },
    });
  }

  const hasRows = !!data?.items?.length;

  return (
    <>
      <div className="data-card">
        <FilterBar
          chips={chips}
          onClearAll={() => setClearSignal((n) => n + 1)}
        />
        <div
          className="table-scroll"
          style={{ minHeight: tableMinHeight(itemsPerPage, true) }}
        >
          <Table bordered={false} className="data-table users-grid">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th className="cell-actions">Actions</th>
              </tr>
              <UserTableFilters
                searchFirstName={firstName}
                setSearchEmail={setEmail}
                searchEmail={email}
                searchLastName={lastName}
                setSearchFirstName={setFirstName}
                setSearchLastName={setLastName}
                setSelectedRoles={setRoleItems}
                userRoles={roleItems}
                roleItems={data?.selections?.Role || ['Member', 'Admin']}
                handleApplyRole={setRoles}
                clearSignal={clearSignal}
              />
            </thead>
            <tbody
              className={isFetching && hasRows ? 'is-refreshing' : undefined}
            >
              {!hasRows ? (
                <TableStateRow
                  colSpan={COLUMNS}
                  loading={isFetching}
                  message="No users match these filters."
                />
              ) : (
                data!.items.map((user) => {
                  const fullName = `${user.firstName || ''} ${
                    user.lastName || ''
                  }`.trim();
                  const isMe =
                    !!currentUser?.email &&
                    currentUser.email.toLowerCase() ===
                      user.email?.toLowerCase();
                  return (
                    <tr key={user.id}>
                      <td>
                        <span className="cell-person">
                          <span className="avatar-sm">
                            {getInitials(fullName, user.email)}
                          </span>
                          {fullName || user.email}
                          {isMe ? <span className="you-tag">you</span> : null}
                        </span>
                      </td>
                      <td className="muted">{user.email}</td>
                      <td>
                        <span
                          className={`role-tag ${
                            user.role === 'Admin' ? 'role-tag--admin' : ''
                          }`}
                        >
                          {user.role}
                        </span>
                      </td>
                      <td className="cell-actions">
                        <span className="row-actions">
                          <button
                            type="button"
                            className="icon-btn"
                            title="Change password"
                            aria-label="Change password"
                            onClick={() =>
                              handleModalOpen({
                                modal: 'update-user-password',
                                data: { ...user },
                              })
                            }
                          >
                            <ChangePasswordIcon />
                          </button>
                          <button
                            type="button"
                            className="icon-btn"
                            title="Edit user"
                            aria-label="Edit user"
                            onClick={() =>
                              handleModalOpen({
                                modal: 'user',
                                data: { ...user },
                              })
                            }
                          >
                            <PencilIcon />
                          </button>
                          <button
                            type="button"
                            className="icon-btn icon-btn--danger"
                            title="Delete user"
                            aria-label="Delete user"
                            onClick={() => setDeleteId(user.id)}
                          >
                            <TrashIcon />
                          </button>
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
        <TablePagination
          currentPage={currentPage}
          totalPages={data?.maxPage}
          onPageChange={setCurrentPage}
          pageSize={itemsPerPage}
          onPageSizeChange={onPageSizeChange}
          isAutoPageSize={isAutoPageSize}
          fitPageSize={fitPageSize}
          total={data?.total}
          itemCount={data?.items?.length ?? 0}
          noun="users"
        />
      </div>
      <DeleteConfirmationModal
        onDelete={handleDelete}
        onHide={() => setDeleteId('')}
        show={!!deleteId}
        id={deleteId}
        isLoading={isDeleting}
      />
    </>
  );
};

export default React.memo(UserTable);
